/**
 * Admin Dashboard Page
 *
 * Overview of site statistics and quick actions.
 */

import { Link } from 'react-router-dom';
import { useFeatureFlags, useAnalyticsSettings } from '../../contexts/SettingsContext';

export function AdminDashboardPage() {
  const features = useFeatureFlags();
  const analytics = useAnalyticsSettings();

  const quickActions = [
    { label: 'Configurar Analytics', path: '/admin/analytics', icon: ChartIcon, description: 'GA4, GTM, Facebook Pixel' },
    { label: 'Configurar SEO', path: '/admin/seo', icon: SearchIcon, description: 'Meta tags, Schema.org' },
    { label: 'Editar Hero Slides', path: '/admin/hero-slides', icon: ImageIcon, description: 'Banners del carrusel' },
    { label: 'Feature Flags', path: '/admin/features', icon: ToggleIcon, description: 'Activar/desactivar funciones' },
    { label: 'Configurar IA', path: '/admin/ai', icon: SparklesIcon, description: 'Proveedor y modelo de IA' },
  ];

  const statusItems = [
    {
      label: 'Google Analytics',
      status: analytics.ga4Enabled && analytics.ga4MeasurementId ? 'active' : 'inactive',
      detail: analytics.ga4MeasurementId ?? 'No configurado',
    },
    {
      label: 'Google Tag Manager',
      status: analytics.gtmEnabled && analytics.gtmContainerId ? 'active' : 'inactive',
      detail: analytics.gtmContainerId ?? 'No configurado',
    },
    {
      label: 'Facebook Pixel',
      status: analytics.fbPixelEnabled && analytics.fbPixelId ? 'active' : 'inactive',
      detail: analytics.fbPixelId ?? 'No configurado',
    },
    {
      label: 'AI Chatbot',
      status: features.aiChatbotEnabled ? 'active' : 'inactive',
      detail: features.aiChatbotEnabled ? 'Activo' : 'Desactivado',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Message */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900">Bienvenido al Panel de Administracion</h2>
        <p className="mt-2 text-gray-600">
          Desde aqui puedes configurar todos los aspectos de tu tienda PetMas.
        </p>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rapidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.path}
              to={action.path}
              className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
            >
              <action.icon className="w-8 h-8 text-orange-500" />
              <h4 className="mt-4 font-medium text-gray-900">{action.label}</h4>
              <p className="mt-1 text-sm text-gray-500">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Status Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado de Integraciones</h3>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Servicio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {statusItems.map((item) => (
                <tr key={item.label}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Feature Flags Summary */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Funcionalidades</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(features).map(([key, value]) => (
            <div
              key={key}
              className={`rounded-lg p-4 text-center ${
                value ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className={`text-2xl ${value ? 'text-green-600' : 'text-gray-400'}`}>
                {value ? '✓' : '○'}
              </div>
              <p className="mt-2 text-xs font-medium text-gray-700 capitalize">
                {formatFeatureName(key)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatFeatureName(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/Enabled$/, '')
    .trim();
}

// Icons
function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ToggleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}
